// A lightweight, non-blocking "working…" indicator: a small spinner pill at the
// bottom of the screen. Use it to make background work legible (the off-thread
// model bake, async placement) — it does NOT block input (pointer-events: none).
//
// Counter-based: each beginBusy() returns a handle; the pill stays up until every
// outstanding handle is done(). A short show-delay means fast operations never
// flash it. NOTE: it can only paint when the main thread is free, so it's for
// ASYNC work — a synchronous block can't repaint mid-block to reveal it.

const CSS = `
@keyframes dt-busy-spin { to { transform: rotate(360deg); } }
@keyframes dt-busy-in { from { opacity: 0; transform: translate(-50%, 6px); } to { opacity: 1; transform: translate(-50%, 0); } }
.dt-busy {
  position: fixed; bottom: 18px; left: 50%; transform: translateX(-50%);
  z-index: 60; display: none; align-items: center; gap: 9px;
  padding: 8px 14px 8px 11px; border-radius: 999px; pointer-events: none;
  background: linear-gradient(180deg, rgba(36,39,50,0.96), rgba(26,28,37,0.96));
  border: 1px solid rgba(255,255,255,0.12);
  box-shadow: 0 10px 30px rgba(0,0,0,0.5), 0 1px 0 rgba(255,255,255,0.06) inset;
  font: 12.5px/1 system-ui, -apple-system, sans-serif; color: #e7eaf2;
  animation: dt-busy-in 0.16s ease-out;
}
.dt-busy.on { display: flex; }
.dt-busy .spin {
  width: 13px; height: 13px; border-radius: 50%; box-sizing: border-box;
  border: 2px solid rgba(255,255,255,0.22); border-top-color: #6fa0ff;
  animation: dt-busy-spin 0.7s linear infinite;
}
@media (prefers-reduced-motion: reduce) {
  .dt-busy { animation: none; }
  .dt-busy .spin { animation-duration: 2s; }
}
`;

const SHOW_DELAY_MS = 150; // don't flash for sub-150ms work

let pill: HTMLDivElement | null = null;
let labelEl: HTMLSpanElement | null = null;
let injected = false;
const active = new Map<number, string>();
let nextId = 1;
let showTimer: ReturnType<typeof setTimeout> | null = null;

function ensure(): void {
  if (pill || typeof document === "undefined") return;
  if (!injected) {
    const s = document.createElement("style");
    s.textContent = CSS;
    document.head.appendChild(s);
    injected = true;
  }
  pill = document.createElement("div");
  pill.className = "dt-busy";
  const spin = document.createElement("div");
  spin.className = "spin";
  labelEl = document.createElement("span");
  pill.append(spin, labelEl);
  document.body.appendChild(pill);
}

function currentLabel(): string {
  let last = "";
  for (const v of active.values()) last = v; // Map keeps insertion order → newest
  return last;
}

function refresh(): void {
  ensure();
  if (!pill || !labelEl) return;
  if (active.size === 0) {
    if (showTimer !== null) {
      clearTimeout(showTimer);
      showTimer = null;
    }
    pill.classList.remove("on");
    return;
  }
  labelEl.textContent = currentLabel(); // keep label fresh even before shown
  if (!pill.classList.contains("on") && showTimer === null) {
    showTimer = setTimeout(() => {
      showTimer = null;
      if (active.size > 0) pill?.classList.add("on");
    }, SHOW_DELAY_MS);
  }
}

export interface BusyHandle {
  done(): void;
}

// Begin a busy operation; returns a handle whose done() ends it. The pill shows
// (after a short delay) while any handle is outstanding, labelled with the most
// recently started one.
export function beginBusy(label: string): BusyHandle {
  const id = nextId++;
  active.set(id, label);
  refresh();
  let ended = false;
  return {
    done(): void {
      if (ended) return;
      ended = true;
      active.delete(id);
      refresh();
    },
  };
}
